import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import { Send as SendIcon, Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { handleApiError } from '../../utils/apiErrorHandler';
import { toast } from '../../utils/toast';

interface WaitlistedAthlete {
  athleteId: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
}

interface ClubWaitlist {
  _id: string;
  clubId: string;
  clubName: string;
  type: 'Elite' | 'Basis';
  isActive: boolean;
  athletes: WaitlistedAthlete[];
}

interface WaitlistPromoteEmailDialogProps {
  open: boolean;
  onClose: () => void;
  competitionId: string;
  club: ClubWaitlist | null;
  athlete: WaitlistedAthlete | null;
  allAthletes: boolean;
  onPromoted: (promotedIds: string[]) => void;
}

interface TemplateData {
  recipientEmail: string;
  recipientName: string;
  bccEmail: string;
  subject: string;
  templateText: string;
  athleteNames: string[];
  competitionName: string;
  clubName: string;
}

const WaitlistPromoteEmailDialog: React.FC<WaitlistPromoteEmailDialogProps> = ({
  open,
  onClose,
  competitionId,
  club,
  athlete,
  allAthletes,
  onPromoted
}) => {
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && club) {
      fetchTemplate();
    }
  }, [open, club, athlete, allAthletes]);

  const fetchTemplate = async () => {
    if (!club) return;
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {
        competitionId,
        clubId: club.clubId,
        type: club.type
      };
      if (!allAthletes && athlete) {
        params.athleteId = athlete.athleteId;
      }

      const response = await axiosInstance.get('/api/waitlist/promote-email-template', { params });
      setTemplateData(response.data);
      setEmailBody(response.data.templateText);
    } catch (err) {
      handleApiError(err, 'Fehler beim Laden der E-Mail-Vorlage.');
      setError('Die E-Mail-Vorlage konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!club || !emailBody.trim()) return;

    try {
      setPromoting(true);

      if (allAthletes) {
        const response = await axiosInstance.post('/api/waitlist/promote-all', {
          competitionId,
          clubId: club.clubId,
          type: club.type,
          emailBody,
          emailSubject: templateData?.subject
        });
        const promotedIds: string[] = response.data.promotedIds || club.athletes.map(a => a.athleteId);
        toast.success(`Alle Athleten von ${club.clubName} (${club.type}) wurden zur Meldeliste hinzugefügt.`);
        onPromoted(promotedIds);
      } else if (athlete) {
        await axiosInstance.post('/api/waitlist/promote', {
          competitionId,
          clubId: club.clubId,
          athleteId: athlete.athleteId,
          type: club.type,
          emailBody,
          emailSubject: templateData?.subject
        });
        toast.success(`${athlete.firstname} ${athlete.surname} wurde zur Meldeliste hinzugefügt.`);
        onPromoted([athlete.athleteId]);
      }

      handleClose();
    } catch (err) {
      handleApiError(err, 'Fehler beim Übernehmen zur Meldeliste');
    } finally {
      setPromoting(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    setError(null);
    setTemplateData(null);
    setEmailBody('');
    onClose();
  };

  const athleteCount = allAthletes ? (club?.athletes.length ?? 0) : 1;

  return (
    <Dialog
      open={open}
      onClose={promoting ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          <Typography variant="h6">
            {allAthletes ? 'Alle Athleten übernehmen & E-Mail senden' : 'Athlet übernehmen & E-Mail senden'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : templateData ? (
          <Box>
            {/* Info-Box */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Wettkampf:</Typography>
                  <Typography variant="body2">{templateData.competitionName}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Verein:</Typography>
                  <Typography variant="body2">{templateData.clubName}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Sportler:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {templateData.athleteNames.map((name, i) => (
                      <Chip key={i} icon={<PersonIcon />} label={name} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* E-Mail Empfänger */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">An (Meldeverantwortlicher):</Typography>
                <Typography variant="body2">{templateData.recipientEmail}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">BCC (Wettkampforganisator):</Typography>
                <Typography variant="body2">{templateData.bccEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Betreff:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{templateData.subject}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* E-Mail-Text */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">E-Mail-Text:</Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(!editMode)}
                  variant={editMode ? 'contained' : 'outlined'}
                >
                  {editMode ? 'Vorschau' : 'Bearbeiten'}
                </Button>
              </Box>

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={12}
                  maxRows={20}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  variant="outlined"
                  placeholder="E-Mail-Text eingeben..."
                />
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'grey.50',
                    minHeight: 250,
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-line',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}
                >
                  {emailBody}
                </Box>
              )}
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              Die E-Mail wird nach der Übernahme automatisch versendet.
            </Alert>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={promoting}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || promoting || !emailBody.trim()}
          startIcon={promoting ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {promoting
            ? 'Wird übernommen...'
            : `${athleteCount === 1 ? 'Athlet' : `Alle ${athleteCount} Athleten`} übernehmen & E-Mail senden`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaitlistPromoteEmailDialog;
